<template>
  <div>
    <validation-observer ref="DriverUserFormValidator">
      <v-card flat>
        <v-toolbar flat>
          <v-toolbar-title>
            {{ $route.params.id ? 'ユーザの編集' : 'ユーザの追加' }}
          </v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="4">
              <v-card outlined>
                <v-card-text style="margin: 0 auto">
                  <v-img
                    :src="user.image_url | load_image_url"
                    max-height="160"
                    contain
                    aspect-ratio="1"
                    class="gray lighten-4 align-center"
                  />
                </v-card-text>
                <v-divider />
                <v-card-text style="margin: 0 auto">
                  <validation-provider
                    v-slot="{ errors }"
                    rules="mimes:image/jpeg,image/png"
                    name="プロフィール画像"
                  >
                    <v-row
                      class="text-center"
                      @dragover.prevent
                      @dragenter="onDragEnter"
                      @dragleave="onDragLeave"
                      @drop="onDrop"
                    >
                      <v-col>
                        <v-file-input
                          v-model="user.avatar"
                          :background-color="isDragging ? 'blue' : 'null'"
                          :clearable="false"
                          :show-size="1000"
                          :error-messages="errors"
                          :prepend-icon="null"
                          accept="image/jpeg,image/png"
                          append-icon="mdi-folder"
                          dense
                          hide-details="auto"
                          hint="画像ファイルを選択して下さい"
                          persistent-hint
                          outlined
                          persistent-placeholder
                        >
                          <template v-slot:selection="{ index, text }">
                            <v-chip
                              v-if="index < 2"
                              color="deep-purple accent-4"
                              dark
                              label
                              small
                            >
                              {{ text }}
                            </v-chip>
                            <span
                              v-else-if="index === 2"
                              class="overline grey--text text--darken-3 mx-2"
                            >+{{ files.length - 2 }} File(s)</span>
                          </template>
                        </v-file-input>
                      </v-col>
                    </v-row>
                  </validation-provider>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="8">
              <v-row>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="user.invisible"
                    label="非表示にする"
                    hide-details="auto"
                  />
                </v-col>
              </v-row>
              <!-- 氏名／フリガナ -->
              <v-row>
                <v-col cols="12" md="6">
                  <validation-provider
                    v-slot="{ errors }"
                    rules="required|full_width|max:12"
                    class="requiredMark"
                    name="氏名"
                  >
                    <v-text-field
                      v-model="user.name"
                      :error-messages="errors"
                      dense
                      label="氏名"
                      hide-details="auto"
                      outlined
                      persistent-placeholder
                      placeholder="例）テスト　太郎"
                      required
                    >
                      <template v-slot:label>
                        <span>氏名</span>
                        <span class="ml-1">
                          ({{ user.name?.length || 0 }} / 12)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
                <v-col cols="12" md="6">
                  <validation-provider
                    v-slot="{ errors }"
                    rules="katakana_only|max:20"
                    name="フリガナ"
                  >
                    <v-text-field
                      v-model="user.name_furigana"
                      :error-messages="errors"
                      dense
                      hide-details="auto"
                      label="フリガナ"
                      outlined
                      persistent-placeholder
                      placeholder="例）テストタロウ"
                    >
                      <template v-slot:label>
                        <span>フリガナ</span>
                        <span class="ml-1">
                          ({{ user.name_furigana?.length || 0 }} / 20)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- /氏名／フリガナ -->

              <!-- ログインID／メールアドレス -->
              <v-row>
                <v-col cols="12" md="6">
                  <validation-provider
                    v-slot="{ errors }"
                    name="ログインID"
                    rules="login_id|min:4|max:255"
                  >
                    <v-text-field
                      v-model="user.login_id"
                      :error-messages="errors"
                      dense
                      autocomplete="off"
                      hide-details="auto"
                      hint="半角英数記号4文字以上、大文字小文字区別なし"
                      persistent-hint
                      label="ログインID"
                      outlined
                      persistent-placeholder
                    >
                      <template v-slot:label>
                        <span>ログインID</span>
                        <span class="ml-1">
                          ({{ user.login_id?.length || 0 }} / 255)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
                <v-col cols="12" md="6">
                  <validation-provider
                    v-slot="{ errors }"
                    rules="email|max:255"
                    name="メールアドレス"
                  >
                    <v-text-field
                      v-model="user.email"
                      :error-messages="errors"
                      dense
                      autocomplete="off"
                      hide-details="auto"
                      label="メールアドレス"
                      outlined
                      persistent-placeholder
                      persistent-hint
                      placeholder="例）test@dxe.jp"
                    >
                      <template v-slot:label>
                        <span>メールアドレス</span>
                        <span class="ml-1">
                          ({{ user.email?.length || 0 }} / 255)
                        </span>
                      </template>
                      <template v-slot:counter></template>
                    </v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- /ログインID／メールアドレス -->

              <!-- ロール -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="user.role"
                    :items="roles"
                    dense
                    label="ロール"
                    hide-details="auto"
                    outlined
                    persistent-placeholder
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <validation-provider
                    v-slot="{ errors }"
                    :rules="{ numeric: true, min_value: 0, max_value: 99999 }"
                    name="ドライバー表示順"
                  >
                    <v-text-field
                      v-if="user.role === 'driver_user'"
                      v-model="user.sort_priority"
                      :error-messages="errors"
                      dense
                      :show-size="1000"
                      label="ドライバー並び順"
                      hide-details="auto"
                      outlined
                      persistent-placeholder
                      reverse
                      type="number"
                      @keydown.69.prevent
                    />
                  </validation-provider>
                </v-col>
              </v-row>
              <!-- /ロール -->

              <!-- ドライバースケジュールを使用する -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="user.use_schedule_app"
                    :disabled="isAllocate || user.role !== 'driver_user'"
                    dense
                    label="ドライバースケジュールを使用する"
                    hide-details="auto"
                    class="pa-0 ma-0"
                  />
                </v-col>
              </v-row>
              <!-- /ドライバースケジュールを使用する -->

              <!-- 受注追加変更を許可 -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="user.app_order_edit"
                    :disabled="isAllocate ||user.role !== 'driver_user'"
                    dense
                    label="受注追加変更を許可"
                    hide-details="auto"
                    class="pa-0 ma-0"
                  />
                </v-col>
              </v-row>
              <!-- /受注追加変更を許可 -->

              <!-- 他ドライバーのスケジュール参照を許可 -->
              <v-row>
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="user.app_schedule_reference"
                    :disabled="isAllocate || user.role !== 'driver_user'"
                    dense
                    label="他ドライバーのスケジュール参照を許可"
                    hide-details="auto"
                    class="pa-0 ma-0"
                  />
                </v-col>
              </v-row>
              <!-- /他ドライバーのスケジュール参照を許可 -->

            </v-col>
          </v-row>

          <!-- 携帯番号 電話番号-->
          <v-row>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ numeric_hyphen_only: true,min: 2, max: 15 }"
                name="携帯番号"
              >
                <v-text-field
                  v-model="user.mobile_number"
                  :error-messages="errors"
                  dense
                  autocomplete="off"
                  hide-details="auto"
                  label="携帯番号"
                  outlined
                  persistent-placeholder
                  placeholder="例）000-0000-0000"
                  hint="携帯番号を「-」ハイフン「( )」込みで入力します"
                  persistent-hint
                >
                  <template v-slot:label>
                    <span>携帯番号</span>
                    <span class="ml-1">
                      ({{ user.mobile_number?.length || 0 }} / 15)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ numeric_hyphen_only: true,min: 2, max: 15 }"
                name="電話番号"
              >
                <v-text-field
                  v-model="user.phone_number"
                  :error-messages="errors"
                  autocomplete="off"
                  dense
                  hint="電話番号を「-」ハイフン「( )」込みで入力します"
                  hide-details="auto"
                  label="電話番号"
                  outlined
                  persistent-placeholder
                  placeholder="例）000-0000-0000"
                  persistent-hint
                >
                  <template v-slot:label>
                    <span>電話番号</span>
                    <span class="ml-1">
                      ({{ user.phone_number?.length || 0 }} / 15)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /携帯番号 電話番号 -->

          <!-- 所属 役職 -->
          <v-row>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                rules="max:20"
                name="所属"
              >
                <v-text-field
                  v-model="user.affiliation"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="所属"
                  outlined
                  persistent-placeholder
                  placeholder="例）管理部"
                >
                  <template v-slot:label>
                    <span>所属</span>
                    <span class="ml-1">
                      ({{ user.affiliation?.length || 0 }} / 20)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                rules="max:20"
                name="役職"
              >
                <v-text-field
                  v-model="user.post"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="役職"
                  outlined
                  persistent-placeholder
                  placeholder="例）部長"
                >
                  <template v-slot:label>
                    <span>役職</span>
                    <span class="ml-1">
                      ({{ user.post?.length || 0 }} / 20)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /所属 役職 -->

          <!-- パスワード -->
          <v-row>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                rules="password"
                vid="password"
                name="パスワード"
              >
                <v-text-field
                  v-model="user.password"
                  :error-messages="errors"
                  :type="showPassword ? 'text' : 'password'"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  dense
                  hide-details="auto"
                  label="パスワード"
                  hint="半角8~12文字 英小文字・大文字・数字をそれぞれ1種類以上"
                  persistent-hint
                  autocomplete="new-password"
                  outlined
                  persistent-placeholder
                  @click:append="showPassword = !showPassword"
                />
              </validation-provider>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: user.password !== user.password_confirmation, confirmed: 'password' }"
                name="パスワード"
              >
                <v-text-field
                  v-model="user.password_confirmation"
                  :error-messages="errors"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  autocomplete="new-password"
                  dense
                  hide-details="auto"
                  label="パスワード（確認用）"
                  outlined
                  required
                  persistent-placeholder
                  @click:append="showConfirmPassword = !showConfirmPassword"
                />
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /パスワード -->

          <!-- パスワードのリセットが必要 -->
          <v-row>
            <v-col cols="12" md="7">
              <v-checkbox
                v-model="user.use_initial_password"
                label="ユーザは次回のログインで新しいパスワードを作成する必要があります"
                :disabled="!user.password"
                hide-details="auto"
                class="pa-0 ma-0"
              />
            </v-col>
          </v-row>
          <!-- /パスワードのリセットが必要 -->
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="cancelButtonClickEvent"
          >
            閉じる
          </v-btn>
          <v-btn
            color="primary"
            @click="registerButtonClickEvent"
          >
            登録
          </v-btn>
        </v-card-actions>
      </v-card>
    </validation-observer>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex'

export default {
  name: 'DriverUserForm',
  components: {},
  mixins: [],
  data () {
    return {
      showPassword: false,
      showConfirmPassword: false,
      roles: [],
      user: {
        id: null,
        invisible: false,
        name: null,
        name_furigana: null,
        affiliation: null,
        post: null,
        phone_number: null,
        mobile_number: null,
        login_id: null,
        role: null,
        email: null,
        avatar: null,
        password: null,
        password_confirmation: null,
        use_initial_password: false,
        sort_priority: null,
        app_order_edit: false,
        use_schedule_app: true,
        app_schedule_reference: false,
        driver_schedule_for_trial: false,
      },
      isDragging: false,
      imageRemoved: false,
      dragCount: 0,
    }
  },
  computed: {
    ...mapState('DriverTenantConfiguration', {
      isAllocate: 'use_allocate',
    }),
    ...mapGetters('DriverAuthentication', [
      'userId'
    ]),
  },

  watch: {
    'user.avatar' : function(newVal) {
      if (newVal) {
        this.getBase64(newVal).then((image) => {
          this.user.image_url = image
        })
      }

    },
    'user.role': function(newVal) {
      // 新規作成時のみ、driver_userが選択された場合にuse_schedule_appをtrueに設定
      if (!this.$route.params.id && newVal === 'driver_user') {
        this.user.use_schedule_app = true
      }
    }
  },
  created() {
    if (this.$route.params.id) {
      this.editMasterUser({ id: this.$route.params.id }).then((content) => {
        this.user = content
        this.roles = content.roles
      })
    } else {
      this.newMasterUser().then((content) => {
        this.user = content
        this.roles = content.roles
      })
    }
  },
  methods: {
    ...mapActions('DriverMasterUser', {
      newMasterUser: 'new',
      editMasterUser: 'edit',
      createMasterUser: 'create',
      updateMasterUser: 'update'
    }),
    registerButtonClickEvent () {
      let $self = this
      this.$refs.DriverUserFormValidator.validate().then((result) => {
        if(result){
          $self.sendRegistrationInformation()
        }
      })
    },
    sendRegistrationInformation(){
      let params = this.buildForm()
      if (this.user.id) {
        this.updateMasterUser({ id: this.user.id, params: params }).then(() => {
          this.$router.push( { name: 'DriverMasterUsers' } )
        })
      } else {
        this.createMasterUser({ params: params }).then(() => {
          this.$router.push( { name: 'DriverMasterUsers', params: { resetPagination: true } } )
        })
      }
    },
    cancelButtonClickEvent () {
      this.$router.push( { name: 'DriverMasterUsers' } )
    },
    buildForm () {
      const params = new FormData()
      params.append('invisible', this._nullToBlank(this.user.invisible))
      params.append('name', this._nullToBlank(this.user.name))
      params.append('name_furigana', this._nullToBlank(this.user.name_furigana))
      params.append('affiliation', this._nullToBlank(this.user.affiliation))
      params.append('post', this._nullToBlank(this.user.post))
      params.append('phone_number', this._nullToBlank(this.user.phone_number))
      params.append('mobile_number', this._nullToBlank(this.user.mobile_number))
      params.append('login_id', this._nullToBlank(this.user.login_id))
      params.append('role', this._nullToBlank(this.user.role))
      params.append('email', this._nullToBlank(this.user.email))
      params.append('password', this._nullToBlank(this.user.password))
      params.append('password_confirmation', this._nullToBlank(this.user.password_confirmation))
      params.append('avatar', this._nullToBlank(this.user.avatar))
      params.append('use_initial_password', this._nullToBlank(this.user.use_initial_password))
      params.append('sort_priority', this._nullToBlank(this.user.sort_priority))
      params.append('app_order_edit', this._nullToBlank(this.user.app_order_edit))
      params.append('app_schedule_reference', this._nullToBlank(this.user.app_schedule_reference))
      params.append('driver_schedule_for_trial', this._nullToBlank(this.user.driver_schedule_for_trial))
      params.append('use_schedule_app', this._nullToBlank(this.user.use_schedule_app))
      return params
    },
    _nullToBlank(value) {
      return (value === null || value === undefined) ? '' : value
    },
    onDrop(e) {
      e.preventDefault();
      e.stopPropagation();
      this.isDragging = false;
      const _files = e.dataTransfer.files;
      this.user.avatar = _files[0];
    },
    onDragEnter(e) {
      e.preventDefault();
      this.isDragging = true;
      this.dragCount++;
    },

    onDragLeave(e) {
      e.preventDefault();
      this.dragCount--;
      if (this.dragCount <= 0) {
        this.isDragging = false;
      }
    },
    getBase64 (file) {
      /* Promiseにアロー関数を渡して非同期処理を書く。*/
      return new Promise((resolve, reject) => {

        /* 組み込みオブジェクトを使い、Base64エンコードされた画像データを取得  */
        const reader = new FileReader()
        reader.readAsDataURL(file)

        /* 成功時と失敗時の処理。resolveの引数に結果を渡して、のちの.then(result => ...)で受け取る  */
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    },

  }
}
</script>
